import React, { useState, useRef, useEffect } from 'react';
import { FileText, Plus, Trash2, Edit, Eye, Image as ImageIcon, Bold, Italic, Underline, AlignLeft, AlignCenter, AlignRight, UploadCloud } from 'lucide-react';
import mammoth from 'mammoth';
import { IOSButton } from '../ui/custom';
import { useTheme } from '../../contexts/ThemeContext';
import { useToast } from '../../hooks/use-toast';
import { PageLayout, PageHeader, PageCard, pageStyles } from '../PageLayout';

const TemplateManagerView = React.memo(({ templates, onSaveTemplate, onDeleteTemplate, currentUser }: any) => {
    const { theme } = useTheme();
    const { toast } = useToast();
    const isDark = theme === 'dark' || (theme === 'system' && window.matchMedia('(prefers-color-scheme: dark)').matches);
    
    const [editingTemplate, setEditingTemplate] = useState<any>(null);
    const [formData, setFormData] = useState({ name: '', content: '', type: 'birthday', format: 'html', docxBase64: '', sourceFileName: '' });
    const [previewTemplate, setPreviewTemplate] = useState<any>(null);
    const [pendingDeleteId, setPendingDeleteId] = useState<string | null>(null);
    const [uploadingDocx, setUploadingDocx] = useState(false);
    const [uploadError, setUploadError] = useState('');
    const [isDragging, setIsDragging] = useState(false);
    const [autoSaveTimeout, setAutoSaveTimeout] = useState<any>(null);
    const [lastSaved, setLastSaved] = useState<Date | null>(null);
    const editorRef = useRef<any>(null);
    const dropZoneRef = useRef<any>(null);

    // Auto-save handler
    useEffect(() => {
        if (!editingTemplate?.id || !formData.content) return;
        
        if (autoSaveTimeout) clearTimeout(autoSaveTimeout);
        
        const timeout = setTimeout(() => {
            onSaveTemplate({ ...formData, id: editingTemplate.id });
            setLastSaved(new Date());
        }, 3000); // 3 Sekunden nach letzter Änderung
        
        setAutoSaveTimeout(timeout);
        
        return () => clearTimeout(timeout);
    }, [formData.content]);

    // Drag & Drop handlers
    useEffect(() => {
        const zone = dropZoneRef.current;
        if (!zone) return;

        const handleDragEnter = (e: DragEvent) => {
            e.preventDefault();
            e.stopPropagation();
            setIsDragging(true);
        };

        const handleDragLeave = (e: DragEvent) => {
            e.preventDefault();
            e.stopPropagation();
            if (e.target === zone) {
                setIsDragging(false);
            }
        };

        const handleDragOver = (e: DragEvent) => {
            e.preventDefault();
            e.stopPropagation();
        };

        const handleDrop = (e: DragEvent) => {
            e.preventDefault();
            e.stopPropagation();
            setIsDragging(false);

            const files = e.dataTransfer?.files;
            if (files && files.length > 0) {
                handleDocxUpload(files[0]);
            }
        };

        zone.addEventListener('dragenter', handleDragEnter);
        zone.addEventListener('dragleave', handleDragLeave);
        zone.addEventListener('dragover', handleDragOver);
        zone.addEventListener('drop', handleDrop);

        return () => {
            zone.removeEventListener('dragenter', handleDragEnter);
            zone.removeEventListener('dragleave', handleDragLeave);
            zone.removeEventListener('dragover', handleDragOver);
            zone.removeEventListener('drop', handleDrop);
        };
    }, []);

    // Content initial setzen, ohne dangerouslySetInnerHTML zu benutzen
    useEffect(() => {
        if (editorRef.current && formData.content !== editorRef.current.innerHTML) {
            const selection = window.getSelection();
            const hadFocus = document.activeElement === editorRef.current;
            
            editorRef.current.innerHTML = formData.content;
            
            if (hadFocus && selection) {
                editorRef.current.focus();
            }
        }
    }, [editingTemplate]); // Nur wenn Template wechselt, nicht bei jedem content-Update

    const readFileAsBase64 = (file: File) => new Promise<string>((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => {
            const result = reader.result as string;
            resolve(result.split(',')[1]);
        };
        reader.onerror = reject;
        reader.readAsDataURL(file);
    });

    const handleDocxUpload = async (file?: File | null) => {
        if (!file) return;
        if (!file.name.toLowerCase().endsWith('.docx')) {
            setUploadError('Bitte eine DOCX-Datei hochladen.');
            return;
        }

        setUploadError('');
        setUploadingDocx(true);

        try {
            const arrayBuffer = await file.arrayBuffer();
            const { value: htmlContent } = await mammoth.convertToHtml({ arrayBuffer }, {
                convertImage: mammoth.images.inline(async (element: any) => {
                    const imageBuffer = await element.read('base64');
                    return { src: `data:${element.contentType};base64,${imageBuffer}` };
                })
            });
            const base64 = await readFileAsBase64(file);

            setFormData(prev => ({
                ...prev,
                name: prev.name || file.name.replace(/\.docx$/i, ''),
                content: htmlContent,
                type: prev.type || 'birthday',
                format: 'docx',
                docxBase64: base64,
                sourceFileName: file.name
            }));
            setEditingTemplate((prev: any) => prev || {});
        } catch (err) {
            console.error('DOCX Upload Fehler', err);
            setUploadError('Vorlage konnte nicht gelesen werden. Bitte prüfe die Datei.');
        } finally {
            setUploadingDocx(false);
        }
    };

    // Format text commands
    const formatText = (command: string, value: string = '') => {
        document.execCommand(command, false, value);
        editorRef.current?.focus();
    };

    // Image upload handler
    const handleImageUpload = () => {
        const input = document.createElement('input');
        input.setAttribute('type', 'file');
        input.setAttribute('accept', 'image/*');
        input.click();

        input.onchange = async () => {
            const file = input.files?.[0];
            if (file) {
                const reader = new FileReader();
                reader.onload = (e) => {
                    const base64Image = e.target?.result as string;
                    document.execCommand('insertHTML', false, `<img src="${base64Image}" style="max-width: 300px; height: auto; margin: 10px 0;" />`);
                };
                reader.readAsDataURL(file);
            }
        };
    };

    const handleSave = async () => {
        if (!formData.name.trim() || !formData.content.trim()) {
            toast({ title: 'Bitte Name und Inhalt ausfüllen', description: 'Beide Felder sind erforderlich, bevor du speichern kannst.', variant: 'destructive' });
            return;
        }

        const templateData = {
            name: formData.name,
            content: formData.content,
            type: formData.type,
            format: formData.format || 'html',
            docxBase64: formData.docxBase64 || '',
            sourceFileName: formData.sourceFileName || '',
            updatedAt: new Date().toISOString(),
            updatedBy: currentUser.name
        };

        if (editingTemplate?.id) {
            await onSaveTemplate({ ...templateData, id: editingTemplate.id });
        } else {
            await onSaveTemplate(templateData);
        }
        
        setEditingTemplate(null);
        setFormData({ name: '', content: '', type: 'birthday', format: 'html', docxBase64: '', sourceFileName: '' });
    };

    const handleEdit = (template: any) => {
        setEditingTemplate(template);
        setFormData({
            name: template.name,
            content: template.content,
            type: template.type || 'birthday',
            format: template.format || 'html',
            docxBase64: template.docxBase64 || '',
            sourceFileName: template.sourceFileName || ''
        });
    };

    const placeholders = [
        { key: '{VORNAME}', desc: 'Vorname des Kunden' },
        { key: '{NAME}', desc: 'Nachname des Kunden' },
        { key: '{ANREDE}', desc: 'Herr/Frau' },
        { key: '{STRASSE}', desc: 'Straße' },
        { key: '{PLZ}', desc: 'Postleitzahl' },
        { key: '{ORT}', desc: 'Ort' },
        { key: '{GEBURTSDATUM}', desc: 'Geburtsdatum' },
        { key: '{MITARBEITER}', desc: 'Name des Mitarbeiters' },
        { key: '{DATUM}', desc: 'Heutiges Datum' },
        { key: '{HEUTE}', desc: 'Heutiges Datum (Alias)' },
        { key: '{KUNDENNUMMER}', desc: 'Interne Kunden-ID' }
    ];

    return (
        <PageLayout>
            <PageHeader
                title="Briefvorlagen"
                subtitle="Vorlagen für Geburtstagsbriefe und andere Anlässe"
                actions={
                    <IOSButton onClick={() => {
                        setFormData({ name: '', content: '', type: 'birthday', format: 'html', docxBase64: '', sourceFileName: '' });
                        setEditingTemplate({});
                    }}>
                        <Plus size={16} /> Neue Vorlage
                    </IOSButton>
                }
            />

            <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
                {templates.map((template: any) => (
                    <div key={template.id} className={`rounded-2xl p-4 shadow-sm hover:shadow-md transition-shadow ${pageStyles.card(isDark)}`}>
                        <div className="flex items-start justify-between mb-3">
                            <div className="flex items-center gap-2">
                                <FileText size={20} className="text-cyan-400" />
                                <div>
                                    <h3 className={`font-bold ${isDark ? 'text-white' : 'text-slate-900'}`}>{template.name}</h3>
                                    <span className={`text-xs capitalize ${isDark ? 'text-white/60' : 'text-slate-600'}`}>{template.type || 'birthday'}</span>
                                </div>
                            </div>
                        </div>
                        
                        <div className={`text-xs mb-3 line-clamp-2 ${isDark ? 'text-white/60' : 'text-slate-600'}`}>
                            <div dangerouslySetInnerHTML={{ __html: (template.content || '').substring(0, 150) + '...' }} />
                        </div>

                        <div className="flex gap-2">
                            <button
                                onClick={() => setPreviewTemplate(template)}
                                className={`flex-1 flex items-center justify-center gap-1 px-3 py-2 text-xs font-semibold rounded-xl border transition-colors ${
                                    isDark 
                                        ? 'text-cyan-300 bg-cyan-500/20 hover:bg-cyan-500/30 border-cyan-400/30' 
                                        : 'text-cyan-700 bg-cyan-50 hover:bg-cyan-100 border-cyan-200'
                                }`}
                            >
                                <Eye size={14} /> Vorschau
                            </button>
                            <button
                                onClick={() => handleEdit(template)}
                                className={`flex items-center justify-center gap-1 px-3 py-2 text-xs font-semibold rounded-xl border transition-colors ${
                                    isDark 
                                        ? 'text-white/70 bg-white/10 hover:bg-white/20 border-white/20' 
                                        : 'text-slate-700 bg-slate-100 hover:bg-slate-200 border-slate-200'
                                }`}
                            >
                                <Edit size={14} />
                            </button>
                            <button
                                onClick={() => setPendingDeleteId(template.id)}
                                className={`flex items-center justify-center gap-1 px-3 py-2 text-xs font-semibold rounded-xl border transition-colors ${
                                    isDark 
                                        ? 'text-red-300 bg-red-500/20 hover:bg-red-500/30 border-red-400/30' 
                                        : 'text-red-700 bg-red-50 hover:bg-red-100 border-red-200'
                                }`}
                            >
                                <Trash2 size={14} />
                            </button>
                        </div>

                        {pendingDeleteId === template.id && (
                            <div className={`mt-3 flex items-center gap-2 text-xs rounded-xl border p-3 ${isDark ? 'border-red-500/30 bg-red-500/10 text-red-100' : 'border-red-200 bg-red-50 text-red-700'}`}>
                                <span className="font-semibold">Vorlage wirklich löschen?</span>
                                <div className="flex gap-2 ml-auto">
                                    <button
                                        onClick={() => { onDeleteTemplate(template.id); setPendingDeleteId(null); toast({ title: 'Vorlage gelöscht', description: template.name }); }}
                                        className={`px-3 py-1 rounded-lg font-semibold ${isDark ? 'bg-red-500/30 hover:bg-red-500/50 text-white' : 'bg-red-600 text-white hover:bg-red-700'}`}
                                    >
                                        Löschen
                                    </button>
                                    <button
                                        onClick={() => setPendingDeleteId(null)}
                                        className={`px-3 py-1 rounded-lg font-semibold border ${isDark ? 'border-white/20 text-white hover:bg-white/10' : 'border-slate-200 text-slate-700 hover:bg-slate-100'}`}
                                    >
                                        Abbrechen
                                    </button>
                                </div>
                            </div>
                        )}

                        {template.updatedAt && (
                            <div className={`text-[10px] mt-2 ${isDark ? 'text-white/40' : 'text-slate-500'}`}>
                                Zuletzt bearbeitet: {new Date(template.updatedAt).toLocaleDateString('de-DE')} von {template.updatedBy}
                            </div>
                        )}
                    </div>
                ))}

                {templates.length === 0 && (
                    <div className={`col-span-full text-center py-10 ${isDark ? 'text-white/50' : 'text-slate-500'}`}>
                        <FileText size={48} className={`mx-auto mb-4 opacity-20 ${isDark ? '' : 'text-slate-400'}`} />
                        <p>Noch keine Vorlagen erstellt</p>
                        <p className="text-sm">Klicke auf "Neue Vorlage" um zu beginnen</p>
                    </div>
                )}
            </div>

            {/* Edit/Create Modal */}
            {editingTemplate && (
                <div className={`fixed inset-0 z-[80] flex items-center justify-center p-4 backdrop-blur-sm ${isDark ? 'bg-gray-900/60' : 'bg-slate-900/20'}`}>
                    <div className={`rounded-3xl p-6 w-full max-w-4xl shadow-2xl max-h-[90vh] overflow-y-auto border ${isDark ? 'glass-card border-white/20' : 'bg-white border-slate-200'}`}>
                        <h3 className={`text-2xl font-bold mb-4 ${isDark ? 'text-white' : 'text-slate-900'}`}>
                            {editingTemplate.id ? 'Vorlage bearbeiten' : 'Neue Vorlage erstellen'}
                        </h3>

                        <div className="grid md:grid-cols-2 gap-6">
                            {/* Form */}
                            <div className="space-y-4">
                                <div 
                                    ref={dropZoneRef}
                                    className={`p-6 border-2 border-dashed rounded-2xl transition-all ${
                                        isDragging 
                                            ? 'border-cyan-400/80 bg-cyan-500/20' 
                                            : isDark ? 'border-white/20 bg-white/5' : 'border-slate-300 bg-slate-50'
                                    }`}
                                >
                                    <div className="flex flex-col items-center justify-center gap-3">
                                        <UploadCloud size={32} className={isDragging ? 'text-cyan-300' : isDark ? 'text-white/50' : 'text-slate-400'} />
                                        <div className="text-center">
                                            <p className={`font-bold text-sm ${isDark ? 'text-white' : 'text-slate-900'}`}>
                                                {isDragging ? 'Datei hier ablegen' : 'Word-Vorlage hochladen (DOCX)'}
                                            </p>
                                            <p className={`text-xs mt-1 ${isDark ? 'text-white/60' : 'text-slate-600'}`}>
                                                Drag & Drop oder klicke zum Auswählen
                                            </p>
                                        </div>
                                        <label className="relative overflow-hidden">
                                            <span className="px-4 py-2 bg-cyan-500/50 text-white rounded-xl text-sm font-semibold cursor-pointer hover:bg-cyan-500/60 inline-block border border-cyan-400/80 transition-colors">
                                                Datei wählen
                                            </span>
                                            <input 
                                                type="file" 
                                                accept=".docx" 
                                                className="absolute inset-0 opacity-0 cursor-pointer" 
                                                onChange={(e) => handleDocxUpload(e.target.files?.[0])} 
                                            />
                                        </label>
                                    </div>
                                    {uploadingDocx && (
                                        <div className="mt-3 text-center">
                                            <div className="inline-block animate-spin rounded-full h-6 w-6 border-b-2 border-cyan-400"></div>
                                            <p className="text-xs text-cyan-300 font-semibold mt-2">Lade hoch und konvertiere...</p>
                                        </div>
                                    )}
                                    {formData.sourceFileName && (
                                        <div className="mt-3 text-center">
                                            <div className="inline-flex items-center gap-2 bg-green-500/20 text-green-300 px-3 py-1 rounded-lg text-xs font-semibold border border-green-400/30">
                                                <FileText size={14} />
                                                {formData.sourceFileName}
                                            </div>
                                        </div>
                                    )}
                                    {uploadError && (
                                        <p className="text-xs text-red-300 mt-3 text-center bg-red-500/20 p-2 rounded border border-red-400/30">{uploadError}</p>
                                    )}
                                </div>

                                {lastSaved && (
                                    <div className="text-xs text-green-300 bg-green-500/20 p-2 rounded-lg text-center border border-green-400/30">
                                        ✓ Automatisch gespeichert um {lastSaved.toLocaleTimeString('de-DE')}
                                    </div>
                                )}

                                <div>
                                    <label className={`text-xs font-bold uppercase block mb-2 ${isDark ? 'text-white/80' : 'text-slate-700'}`}>Vorlagenname</label>
                                    <input
                                        type="text"
                                        value={formData.name}
                                        onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                                        className={`w-full rounded-xl p-3 text-sm border ${
                                            isDark 
                                                ? 'glass-input border-white/20 text-white placeholder-white/50' 
                                                : 'bg-slate-50 border-slate-200 text-slate-900 placeholder-slate-400'
                                        }`}
                                        placeholder="z.B. Geburtstagsbrief Standard"
                                    />
                                </div>

                                <div>
                                    <label className={`text-xs font-bold uppercase block mb-2 ${isDark ? 'text-white/80' : 'text-slate-700'}`}>Typ</label>
                                    <select
                                        value={formData.type}
                                        onChange={(e) => setFormData({ ...formData, type: e.target.value })}
                                        className={`w-full rounded-xl p-3 text-sm cursor-pointer border ${
                                            isDark 
                                                ? 'glass-input border-white/20 text-white bg-white/5' 
                                                : 'bg-slate-50 border-slate-200 text-slate-900'
                                        }`}
                                    >
                                        <option value="birthday">Geburtstag</option>
                                        <option value="anniversary">Jubiläum</option>
                                        <option value="general">Allgemein</option>
                                    </select>
                                </div>

                                <div>
                                    <label className={`text-xs font-bold uppercase block mb-3 ${isDark ? 'text-white/80' : 'text-slate-700'}`}>Briefinhalt erstellen</label>
                                    
                                    {/* Word-like Toolbar */}
                                    <div className={`rounded-t-xl p-3 shadow-sm border ${isDark ? 'glass-card bg-white/5 border-white/10' : 'bg-slate-50 border-slate-200'}`}>
                                        <div className="flex gap-1 flex-wrap items-center">
                                            {/* Font Size */}
                                            <select onChange={(e) => formatText('fontSize', e.target.value)} className={`px-2 py-1 rounded text-sm cursor-pointer border ${
                                                isDark 
                                                    ? 'glass-input border-white/20 hover:bg-white/10 text-white bg-white/5' 
                                                    : 'bg-white border-slate-200 text-slate-900 hover:bg-slate-50'
                                            }`} defaultValue="3">
                                                <option value="1">Klein</option>
                                                <option value="3">Normal</option>
                                                <option value="5">Groß</option>
                                                <option value="7">Sehr groß</option>
                                            </select>
                                            
                                            <div className="w-px h-6 bg-gray-300 mx-2"></div>
                                            
                                            {/* Text Formatting */}
                                            <button type="button" onClick={() => formatText('bold')} className={`p-2 rounded transition-colors ${isDark ? 'hover:bg-white/10' : 'hover:bg-slate-200'}`} title="Fett (Strg+B)">
                                                <Bold size={18} className={isDark ? 'text-white/80' : 'text-slate-700'} />
                                            </button>
                                            <button type="button" onClick={() => formatText('italic')} className={`p-2 rounded transition-colors ${isDark ? 'hover:bg-white/10' : 'hover:bg-slate-200'}`} title="Kursiv (Strg+I)">
                                                <Italic size={18} className={isDark ? 'text-white/80' : 'text-slate-700'} />
                                            </button>
                                            <button type="button" onClick={() => formatText('underline')} className={`p-2 rounded transition-colors ${isDark ? 'hover:bg-white/10' : 'hover:bg-slate-200'}`} title="Unterstrichen (Strg+U)">
                                                <Underline size={18} className={isDark ? 'text-white/80' : 'text-slate-700'} />
                                            </button>
                                            
                                            <div className="w-px h-6 bg-gray-300 mx-2"></div>
                                            
                                            {/* Text Color */}
                                            <input type="color" onChange={(e) => formatText('foreColor', e.target.value)} className="w-8 h-8 glass-input border-white/20 rounded cursor-pointer" title="Textfarbe" />
                                            <input type="color" onChange={(e) => formatText('backColor', e.target.value)} className="w-8 h-8 glass-input border-white/20 rounded cursor-pointer" title="Hintergrundfarbe" defaultValue="#ffffff" />
                                            
                                            <div className="w-px h-6 bg-gray-300 mx-2"></div>
                                            
                                            {/* Alignment */}
                                            <button type="button" onClick={() => formatText('justifyLeft')} className="p-2 hover:bg-white/10 rounded transition-colors" title="Linksbündig">
                                                <AlignLeft size={18} className="text-white/80" />
                                            </button>
                                            <button type="button" onClick={() => formatText('justifyCenter')} className="p-2 hover:bg-white/10 rounded transition-colors" title="Zentriert">
                                                <AlignCenter size={18} className="text-white/80" />
                                            </button>
                                            <button type="button" onClick={() => formatText('justifyRight')} className="p-2 hover:bg-white/10 rounded transition-colors" title="Rechtsbündig">
                                                <AlignRight size={18} className="text-white/80" />
                                            </button>
                                            
                                            <div className="w-px h-6 bg-gray-300 mx-2"></div>
                                            
                                            {/* Lists */}
                                            <button type="button" onClick={() => formatText('insertUnorderedList')} className="px-2 py-2 hover:bg-white/10 rounded text-sm transition-colors" title="Aufzählung">
                                                <span className="text-white/80">• Liste</span>
                                            </button>
                                            <button type="button" onClick={() => formatText('insertOrderedList')} className="px-2 py-2 hover:bg-white/10 rounded text-sm transition-colors" title="Nummerierung">
                                                <span className="text-white/80">1. Liste</span>
                                            </button>
                                            
                                            <div className="w-px h-6 bg-gray-300 mx-2"></div>
                                            
                                            {/* Headings */}
                                            <button type="button" onClick={() => formatText('formatBlock', '<h1>')} className="px-3 py-1 hover:bg-white/10 rounded text-sm font-bold transition-colors text-white/80" title="Überschrift 1">
                                                H1
                                            </button>
                                            <button type="button" onClick={() => formatText('formatBlock', '<h2>')} className="px-3 py-1 hover:bg-white/10 rounded text-sm font-bold transition-colors text-white/80" title="Überschrift 2">
                                                H2
                                            </button>
                                            <button type="button" onClick={() => formatText('formatBlock', '<p>')} className="px-3 py-1 hover:bg-white/10 rounded text-sm transition-colors text-white/80" title="Normal">
                                                P
                                            </button>
                                            
                                            <div className="w-px h-6 bg-gray-300 mx-2"></div>
                                            
                                            {/* Image Upload */}
                                            <button type="button" onClick={handleImageUpload} className="flex items-center gap-2 px-3 py-2 bg-cyan-500/50 hover:bg-cyan-500/60 text-white rounded transition-colors font-medium border border-cyan-400/80" title="Bild/Logo einfügen">
                                                <ImageIcon size={18} />
                                                <span className="text-sm">Logo einfügen</span>
                                            </button>
                                        </div>
                                    </div>

                                    {/* Word-like Page Container */}
                                    <div className="bg-gray-900 p-8 border-x border-b border-white/10 rounded-b-xl" style={{ minHeight: '600px' }}>
                                        {/* A4 Page */}
                                        <div className="bg-white shadow-lg mx-auto" style={{ width: '210mm', minHeight: '297mm', padding: '20mm' }}>
                                            <div
                                                ref={editorRef}
                                                contentEditable
                                                suppressContentEditableWarning
                                                onInput={(e: any) => {
                                                    setFormData({ ...formData, content: e.currentTarget.innerHTML });
                                                }}
                                                className="focus:outline-none min-h-[257mm]"
                                                style={{ 
                                                    fontSize: '12pt',
                                                    lineHeight: '1.6',
                                                    fontFamily: 'Arial, sans-serif'
                                                }}
                                            />
                                        </div>
                                    </div>
                                    
                                    <div className="mt-3 p-3 glass-card bg-cyan-500/20 border border-cyan-400/30 rounded-xl">
                                        <p className="text-xs text-cyan-300 font-semibold mb-1">💡 Platzhalter verwenden:</p>
                                        <p className="text-xs text-cyan-200">
                                            <code className="bg-white/10 px-1.5 py-0.5 rounded mx-1 text-white/80">{'{VORNAME}'}</code>
                                            <code className="bg-white/10 px-1.5 py-0.5 rounded mx-1 text-white/80">{'{NAME}'}</code>
                                            <code className="bg-white/10 px-1.5 py-0.5 rounded mx-1 text-white/80">{'{ANREDE}'}</code>
                                            <code className="bg-white/10 px-1.5 py-0.5 rounded mx-1 text-white/80">{'{DATUM}'}</code>
                                            <code className="bg-white/10 px-1.5 py-0.5 rounded mx-1 text-white/80">{'{MITARBEITER}'}</code>
                                        </p>
                                    </div>
                                </div>

                                <div className="flex gap-3 pt-2">
                                    <button
                                        onClick={() => {
                                            setEditingTemplate(null);
                                            setFormData({ name: '', content: '', type: 'birthday', format: 'html', docxBase64: '', sourceFileName: '' });
                                        }}
                                        className={`flex-1 rounded-xl border py-3 text-sm font-semibold transition-colors ${
                                            isDark 
                                                ? 'border-white/20 text-white/70 hover:bg-white/5' 
                                                : 'border-slate-200 text-slate-700 hover:bg-slate-50'
                                        }`}
                                    >
                                        Abbrechen
                                    </button>
                                    <button
                                        onClick={handleSave}
                                        className={`flex-1 rounded-xl py-3 text-sm font-semibold shadow border transition-colors ${
                                            isDark 
                                                ? 'bg-cyan-500/50 hover:bg-cyan-500/60 text-white border-cyan-400/80' 
                                                : 'bg-cyan-600 hover:bg-cyan-700 text-white border-cyan-600'
                                        }`}
                                    >
                                        Speichern
                                    </button>
                                </div>
                            </div>

                            {/* Placeholders Reference */}
                            <div>
                                <h4 className={`text-sm font-bold uppercase mb-3 ${isDark ? 'text-white/80' : 'text-slate-700'}`}>Verfügbare Platzhalter</h4>
                                <div className={`space-y-2 rounded-xl p-4 max-h-[400px] overflow-y-auto border ${
                                    isDark ? 'glass-card bg-white/5 border-white/10' : 'bg-slate-50 border-slate-200'
                                }`}>
                                    {placeholders.map((p) => (
                                        <div key={p.key} className="flex items-start gap-2 text-sm">
                                            <code className={`px-2 py-1 rounded font-mono text-xs whitespace-nowrap border ${
                                                isDark 
                                                    ? 'glass-card bg-cyan-500/20 text-cyan-300 border-cyan-400/30' 
                                                    : 'bg-cyan-50 text-cyan-700 border-cyan-200'
                                            }`}>
                                                {p.key}
                                            </code>
                                            <span className={`text-xs ${isDark ? 'text-white/70' : 'text-slate-600'}`}>{p.desc}</span>
                                        </div>
                                    ))}
                                </div>

                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Preview Modal */}
            {previewTemplate && (
                <div className={`fixed inset-0 z-[80] flex items-center justify-center p-4 backdrop-blur-sm ${isDark ? 'bg-gray-900/60' : 'bg-slate-900/20'}`}>
                    <div className={`rounded-3xl p-6 w-full max-w-2xl shadow-2xl max-h-[90vh] overflow-y-auto border ${
                        isDark ? 'glass-card border-white/20' : 'bg-white border-slate-200'
                    }`}>
                        <div className="flex justify-between items-center mb-4">
                            <h3 className={`text-2xl font-bold ${isDark ? 'text-white' : 'text-slate-900'}`}>{previewTemplate.name}</h3>
                            <button
                                onClick={() => setPreviewTemplate(null)}
                                className={`p-2 rounded-full transition-colors border ${
                                    isDark 
                                        ? 'glass-card bg-white/10 hover:bg-white/20 border-white/20' 
                                        : 'bg-slate-100 hover:bg-slate-200 border-slate-200'
                                }`}
                            >
                                ✕
                            </button>
                        </div>

                        <div className={`rounded-xl p-6 border ${isDark ? 'glass-card bg-white/5 border-white/10' : 'bg-slate-50 border-slate-200'}`}>
                            <div 
                                className="prose prose-sm max-w-none"
                                dangerouslySetInnerHTML={{ __html: previewTemplate.content }}
                            />
                        </div>

                        <div className={`mt-4 text-xs ${isDark ? 'text-white/60' : 'text-slate-600'}`}>
                            Typ: <span className="capitalize">{previewTemplate.type || 'birthday'}</span>
                            {previewTemplate.updatedAt && (
                                <> • Zuletzt bearbeitet: {new Date(previewTemplate.updatedAt).toLocaleDateString('de-DE')}</>
                            )}
                        </div>
                    </div>
                </div>
            )}
        </PageLayout>
    );
});

export default TemplateManagerView;
